import { useCallback, useEffect } from 'react';

import type { BuckChatConfig, LtPlusBillingInterval, StripeConfig } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';
import type { AnalyticsHandler } from '../../SignupDrawer/hooks/useSignupFlow';
import { getLtPlusPlanAmount } from '../ltPlusPlans';
import { useLtPlusCheckout } from './useLtPlusCheckout';

type UseCheckoutStepOptions = {
  stripeConfig: StripeConfig;
  buckChatConfig: BuckChatConfig;
  billingInterval: LtPlusBillingInterval;
  onSuccess: () => void;
  onBack: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

type UseCheckoutStepReturn = {
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  clearError: () => void;
  handleBack: () => void;
  displayPrice: number;
};

export const useCheckoutStep = ({
  stripeConfig,
  buckChatConfig,
  billingInterval,
  onSuccess,
  onBack,
  onAnalyticsEvent,
}: UseCheckoutStepOptions): UseCheckoutStepReturn => {
  useEffect(() => {
    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_PAYWALL_VIEWED, {
      [ANALYTICS_KEYS.BILLING_INTERVAL]: billingInterval,
    });
  }, [billingInterval, onAnalyticsEvent]);

  const { handleSubmit, isSubmitting, error, clearError } = useLtPlusCheckout({
    buckChatConfig,
    billingInterval,
    onSuccess,
    // Error is exposed via state and consumed by the UI.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onError: () => {},
    onAnalyticsEvent,
  });

  const displayPrice = getLtPlusPlanAmount(stripeConfig, billingInterval) / 100;

  const handleBack = useCallback(() => {
    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_CHECKOUT_BACK_CLICKED, {
      [ANALYTICS_KEYS.BILLING_INTERVAL]: billingInterval,
    });
    onBack();
  }, [billingInterval, onAnalyticsEvent, onBack]);

  return {
    handleSubmit,
    isSubmitting,
    error,
    clearError,
    handleBack,
    displayPrice,
  };
};
